import { unreadable } from './shared.ts';

/**
 * §8's History page.
 *
 * The only notice left is `malformed`, and it is here because it is the one case where the grid cannot
 * speak for itself: rows that could not be read are rows the page never draws, so nothing on screen
 * would say they were missing. The count of dead videos went the other way (2026-08-07) — every one of
 * those rows already wears `unavailableMarker` in place, so a banner over the grid only tallied what
 * the reader could see.
 */
export const history = {
  notice: {
    malformed: 'Your history could not be read, so there is nothing to show from it.',
  },

  /** A dead row. "Watched" would be noise here — every row on this page is watched by construction. */
  unavailableMarker: {
    label: 'Unavailable',
    title:
      'This video no longer exists. It is still in your history because nothing removes an entry automatically.',
  },

  malformedList: {
    title: 'Could not read your history',
    detail: 'The instance answered with something Visious could not read.',
    retryable: true,
  },

  empty: {
    title: 'Nothing watched yet',
    detail:
      'Videos appear here once you have watched about half a minute of them, or a quarter of a short one. It is recorded in your account on the instance, so it follows you between browsers.',
  },

  problem: {
    notFound: {
      title: 'This instance keeps no watch history',
      detail: 'It is not served here. Some instances leave parts of the app out.',
      retryable: false,
    },
    unreadable: unreadable('Could not read your history'),
    disabled: {
      title: 'Watch history is switched off on this instance',
      detail:
        'Its administrator turned it off, so there is nothing to show. Nothing is being recorded either.',
      retryable: false,
    },
  },

  page: {
    heading: 'History',
    lede:
      'What you have watched on your account, most recently marked first.',
    clear: 'Clear',
    /** The armed Clear button's title — there is no panel any more, so this is where the stakes live. */
    confirmDetail:
      'This removes it from your account on the instance, not just from Visious, and there is no way to undo it.',
    clearing: 'Clearing…',
    loading: 'Loading your watch history…',
    listLabel: 'Watch history',
    verified: 'Verified',
    loadMore: (soFar: number): string => `Load more (${soFar} so far)`,
    loadingMore: 'Loading…',
    remove: (title: string): string => `Remove ${title} from your history`,
    /** The corner button's hover word — the act alone; the card states the title itself. */
    removeTitle: 'Remove from history',
    removing: 'Removing from history',
  },

  /**
   * The two writes. Neither goes through the playlist write copy — that one is about a request body,
   * and these two have none.
   */
  write: {
    /**
     * The guard behind CLAUDE.md's `204` trap: an id this build does not recognise would build the
     * clear-everything URL. What the reader gets is the *consequence* — nothing was touched, and the
     * refusal is protecting the rest of the history.
     */
    badVideoId:
      'Visious did not recognise that video, and would not send a request that could clear your whole history instead of removing one entry. Nothing was changed.',
    badVideoIdTitle: 'Visious did not send that',
    refusedRemove: 'The instance refused that removal',
    refusedClear: 'The instance refused to clear the history',
    refusedDetail:
      'Nothing was changed. This is a bug here, not a problem with your account, so signing in again will not help.',
  },
} as const;
